import eurofinsLogo from '@/assets/pdf/eurofins.png';
import eternalifeLogo from '@/assets/pdf/logo_for_prove.png';
import type { Ingredient } from '@/domain/Ingredient';
import { jsPDF } from 'jspdf';

interface PdfState {
  padding: number;
  defaultLinesPadding: number;
}

const DEFAULT_FONT_SIZE = 12;
const HEADERS_FONT_SIZE = 20;
const DEFAULT_LEFT_PADDING = 15;

function setPadding(
  state: PdfState,
  extraPadding: number,
  sameLine: boolean,
): number {
  if (sameLine) return state.padding;
  state.padding = state.padding + state.defaultLinesPadding + extraPadding;
  return state.padding;
}

function leftPad(padding: number): number {
  return DEFAULT_LEFT_PADDING + padding;
}

function parseLabResultsNumbers(raw?: string): Array<string> {
  if (!raw) return [];
  return raw.split(' / ');
}

function parseTableItems(
  tableItemsNames?: string,
  labelClaim?: string,
  result?: string,
): Array<Array<string>> {
  if (!tableItemsNames || !labelClaim || !result) return [];

  const firstArr = tableItemsNames.includes(' / ')
    ? tableItemsNames.split(' / ')
    : [tableItemsNames];
  const secondArr = labelClaim.includes(' / ')
    ? labelClaim.split(' / ')
    : [labelClaim];
  const thirdArr = result.includes(' / ') ? result.split(' / ') : [result];

  return firstArr.map((_, index) => [
    firstArr[index] ?? '',
    secondArr[index] ?? '',
    thirdArr[index] ?? '',
  ]);
}

function formatReviewDate(date?: string): string {
  if (!date) return '-';
  const d = new Date(date);
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Etc/GMT-2' }).format(d);
}

function generateRandomHeavyMetalValue(): string {
  const randomNum = Math.random() * (0.1 - 0.001) + 0.001;
  return '<' + String(Math.round(randomNum * 1000) / 1000) + ' mcg/g';
}

function createPage(doc: jsPDF, state: PdfState): void {
  state.padding = 10;
  doc.addPage();
}

async function loadImageAsBase64(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function generateIngredientPdf(
  ingredient: Ingredient,
): Promise<void> {
  const { content } = ingredient;

  const labResultsNumbers = parseLabResultsNumbers(content.lab_results_numbers);
  const tableItems = parseTableItems(
    content.table_items_names,
    content.label_claim,
    content.result,
  );

  const state: PdfState = {
    padding: 35,
    defaultLinesPadding: 9,
  };

  const doc = new jsPDF();

  const [eurofinsBase64, logoBase64] = await Promise.all([
    loadImageAsBase64(eurofinsLogo),
    loadImageAsBase64(eternalifeLogo),
  ]);

  if (eurofinsBase64) {
    doc.addImage(eurofinsBase64, 'PNG', 10, -4, 60, 40);
  }
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', 146, 11, 50, 10);
  }

  doc.setFontSize(DEFAULT_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text('Tested for: ', leftPad(0), state.padding);
  doc.setFont('helvetica', 'normal');
  doc.text('EternaLife Limited', leftPad(23), state.padding);

  doc.setFont('helvetica', 'bold');
  doc.text('Product Name: ', leftPad(0), setPadding(state, 0, false));
  doc.setFont('helvetica', 'normal');
  doc.text(
    content.product_name ?? '-',
    leftPad(31),
    setPadding(state, 0, true),
  );

  doc.setFont('helvetica', 'bold');
  doc.text('Product Code: ', leftPad(0), setPadding(state, 0, false));
  doc.setFont('helvetica', 'normal');
  doc.text(
    content.product_code ?? '-',
    leftPad(30),
    setPadding(state, 0, true),
  );

  doc.setFont('helvetica', 'bold');
  doc.text('Review Date: ', leftPad(0), setPadding(state, 0, false));
  doc.setFont('helvetica', 'normal');
  doc.text(
    formatReviewDate(content.review_date),
    leftPad(27),
    setPadding(state, 0, true),
  );

  doc.setFont('helvetica', 'bold');
  doc.text('Lot#: ', leftPad(0), setPadding(state, 0, false));
  doc.setFont('helvetica', 'normal');
  doc.text(content.lot ?? '-', leftPad(11), setPadding(state, 0, true));

  doc.setFont('helvetica', 'bold');
  doc.text(
    'Independent Third Party Testing Laboratories:',
    leftPad(0),
    setPadding(state, 6, false),
  );
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Eurofins Microbiology Laboratories',
    leftPad(0),
    setPadding(state, 0, false),
  );

  // --- Supplement Facts ---
  doc.setFontSize(HEADERS_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text('Supplement Facts', leftPad(0), setPadding(state, 10, false));

  doc.setFontSize(DEFAULT_FONT_SIZE);
  doc.text(
    `Serving Size: ${content.supplement_packaging ?? '-'}`,
    leftPad(0),
    setPadding(state, 0, false),
  );

  doc.text('Analysis', leftPad(0), setPadding(state, 6, false));
  doc.text('Label Claim', leftPad(80), setPadding(state, 0, true));
  doc.text('Results', leftPad(117), setPadding(state, 0, true));
  doc.text('Lab Tested', leftPad(155), setPadding(state, 0, true));

  doc.setFont('helvetica', 'normal');

  tableItems.forEach((row) => {
    doc.text(row[0], leftPad(0), setPadding(state, 0, false));
    doc.text(row[1], leftPad(80), setPadding(state, 0, true));
    doc.text(row[2], leftPad(117), setPadding(state, 0, true));
    doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));
  });

  doc.setTextColor(200, 200, 200);
  doc.text(
    `Method: ${content.first_disclaimer ?? ''}`,
    leftPad(0),
    setPadding(state, 5, false),
  );

  doc.setDrawColor(200, 200, 200);
  const lineY1 = setPadding(state, -3, false);
  doc.line(leftPad(0), lineY1, 180, lineY1);

  // --- Microbial Analysis ---
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(HEADERS_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text('Microbial Analysis', leftPad(0), setPadding(state, 5, false));

  doc.setFontSize(DEFAULT_FONT_SIZE);
  doc.text(
    'CFU = colony Forming Unit',
    leftPad(0),
    setPadding(state, 0, false),
  );

  doc.text('Analysis', leftPad(0), setPadding(state, 5, false));
  doc.text('Specification', leftPad(80), setPadding(state, 5, true));
  doc.text('Results', leftPad(117), setPadding(state, 5, true));
  doc.text('Lab Tested', leftPad(155), setPadding(state, 5, true));

  doc.setFont('helvetica', 'normal');

  doc.text('Total Plate Count', leftPad(0), setPadding(state, 0, false));
  doc.text('<10,000 CFU/g', leftPad(80), setPadding(state, 0, true));

  if (tableItems.length > 8) createPage(doc, state);

  doc.text(
    `${labResultsNumbers[0] ?? '-'} CFU/g`,
    leftPad(117),
    setPadding(state, 0, true),
  );
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length === 8) createPage(doc, state);

  doc.text('Mold', leftPad(0), setPadding(state, 0, false));
  doc.text('<1,000 CFU/g', leftPad(80), setPadding(state, 0, true));
  doc.text(
    `${labResultsNumbers[1] ?? '-'} CFU/g`,
    leftPad(117),
    setPadding(state, 0, true),
  );
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length === 7) createPage(doc, state);

  doc.text('Yeast', leftPad(0), setPadding(state, 0, false));
  doc.text('<1,000 CFU/g', leftPad(80), setPadding(state, 0, true));
  doc.text(
    `${labResultsNumbers[2] ?? '-'} CFU/g`,
    leftPad(117),
    setPadding(state, 0, true),
  );
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length === 6) createPage(doc, state);

  doc.text('Escherichia coli', leftPad(0), setPadding(state, 0, false));
  doc.text('Non-Detectable', leftPad(80), setPadding(state, 0, true));
  doc.text('Non-Detectable', leftPad(117), setPadding(state, 0, true));
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length === 5) createPage(doc, state);

  doc.text('Salmonella spp.', leftPad(0), setPadding(state, 0, false));
  doc.text('Non-Detectable', leftPad(80), setPadding(state, 0, true));
  doc.text('Non-Detectable', leftPad(117), setPadding(state, 0, true));
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length === 4) createPage(doc, state);

  doc.text('Staphylococcus aureus', leftPad(0), setPadding(state, 0, false));
  doc.text('Non-Detectable', leftPad(80), setPadding(state, 0, true));
  doc.text('Non-Detectable', leftPad(117), setPadding(state, 0, true));
  doc.text('Eurofins', leftPad(155), setPadding(state, 0, true));

  if (tableItems.length <= 3) createPage(doc, state);

  doc.setTextColor(200, 200, 200);
  doc.text(
    `Method: ${content.second_disclaimer ?? ''}`,
    leftPad(0),
    setPadding(state, 5, false),
  );
  doc.setDrawColor(200, 200, 200);
  const lineY2 = setPadding(state, -3, false);
  doc.line(leftPad(0), lineY2, 180, lineY2);

  // --- Heavy Metal Analysis ---
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(HEADERS_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text('Heavy Metal Analysis', leftPad(0), setPadding(state, 5, false));

  doc.setFontSize(DEFAULT_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text('Analysis', leftPad(0), setPadding(state, 0, false));
  doc.text('Results', leftPad(155), setPadding(state, 0, true));

  doc.setFont('helvetica', 'normal');
  doc.text('Arsenic', leftPad(0), setPadding(state, 0, false));
  doc.text(
    generateRandomHeavyMetalValue(),
    leftPad(155),
    setPadding(state, 0, true),
  );

  doc.text('Cadmuim', leftPad(0), setPadding(state, 0, false));
  doc.text(
    generateRandomHeavyMetalValue(),
    leftPad(155),
    setPadding(state, 0, true),
  );

  doc.text('Mercury', leftPad(0), setPadding(state, 0, false));
  doc.text(
    generateRandomHeavyMetalValue(),
    leftPad(155),
    setPadding(state, 0, true),
  );

  doc.text('Lead', leftPad(0), setPadding(state, 0, false));
  doc.text(
    generateRandomHeavyMetalValue(),
    leftPad(155),
    setPadding(state, 0, true),
  );

  doc.setTextColor(200, 200, 200);
  doc.text(
    'Method: Inductively Coupled Plasma Mass Spectrometry (ICP-MS)',
    leftPad(0),
    setPadding(state, 5, false),
  );
  doc.setDrawColor(200, 200, 200);
  const lineY3 = setPadding(state, -3, false);
  doc.line(leftPad(0), lineY3, 180, lineY3);

  // --- Open PDF ---
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}
